import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './SchoolsDirectory.css';

const SchoolsDirectory = () => {
  const { user } = useContext(AuthContext);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    city: '',
    board_type: '',
    search: ''
  });
  
  const isParent = user && user.user_type === 'parent';

  // Initial fetch on mount
  useEffect(() => {
    fetchSchools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch when filters change (debounced to prevent too many calls on mobile)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!loading) {
        fetchSchools();
      }
    }, 300); // 300ms debounce for mobile performance

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.city, filters.board_type, filters.search]);

  const fetchSchools = async () => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams();
      if (filters.city) params.append('city', filters.city);
      if (filters.board_type) params.append('board_type', filters.board_type);
      if (filters.search) params.append('search', filters.search);

      const queryString = params.toString();
      const url = queryString ? `/schools?${queryString}` : '/schools';
      
      console.log('Fetching schools from:', url);
      const response = await api.get(url);
      
      if (response.data && response.data.schools) {
        setSchools(response.data.schools);
        console.log('Schools loaded:', response.data.schools.length);
      } else {
        console.error('Unexpected response format:', response.data);
        setError('Invalid response from server. Please try again.');
        setSchools([]);
      }
    } catch (err) {
      console.error('Error fetching schools:', err);
      console.error('Error details:', {
        message: err.message,
        code: err.code,
        response: err.response,
        isNetworkError: err.isNetworkError,
        config: err.config
      });
      
      let errorMessage = 'Failed to load schools.';
      
      if (err.isNetworkError || !err.response) {
        // Network error - no connection to server
        errorMessage = 'Network Error: Unable to connect to server. Please check your internet connection and try again.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server Error: Please try again later.';
      } else if (err.response?.status === 404) {
        errorMessage = 'Schools endpoint not found. Please contact support.';
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setSchools([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  // Get unique cities and board types for filter options
  const uniqueCities = [...new Set(schools.map(s => s.city).filter(Boolean))].sort();
  const uniqueBoards = [...new Set(schools.map(s => s.board_type).filter(Boolean))].sort();

  // Get default school image based on board type - using local images
  const getDefaultSchoolImage = (boardType) => {
    const images = {
      'CBSE': '/images/school-cbse.jpg',
      'ICSE': '/images/school-icse.jpg',
      'State Board': '/images/school-state-board.jpg',
      'IB': '/images/school-cbse.jpg',
      'Karnataka State': '/images/school-state-board.jpg'
    };
    return images[boardType] || '/images/school-cbse.jpg';
  };

  return (
    <div className="App">
      <Header />
      <div 
        className="page schools-page"
        style={{
          backgroundImage: 'url(/login.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed'
        }}
      >
        {/* Animated Background Elements */}
        <div className="schools-animated-bg">
          <div className="schools-floating-shape schools-shape-1"></div>
          <div className="schools-floating-shape schools-shape-2"></div>
          <div className="schools-floating-shape schools-shape-3"></div>
          <div className="schools-gradient-orb schools-orb-1"></div>
          <div className="schools-gradient-orb schools-orb-2"></div>
        </div>

        <div className="container">
          {/* Hero Section */}
          <div className="schools-hero">
            <div className="schools-hero-content">
              <div className="schools-hero-badge">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                <span>Explore Schools</span>
              </div>
              <h1 className="schools-hero-title">
                <span className="hero-title-main">{isParent ? 'Your' : 'Find Your'}</span>
                <span className="hero-title-highlight">{isParent ? 'Assigned School' : 'Perfect School'}</span>
              </h1>
              <p className="schools-hero-description">
                {isParent 
                  ? 'View your assigned school and browse available book kits for your child.'
                  : 'Discover schools across different boards and cities. Browse through our curated list of educational institutions.'}
              </p>
            </div>
          </div>

          {/* Modern Filters Section - Hide for parents */}
          {!isParent && (
            <div className="filters-section-modern">
              <div className="filters-header">
                <h2 className="filters-title">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="M21 21l-4.35-4.35"></path>
                  </svg>
                  Filter Schools
                </h2>
              </div>
              <div className="filters-grid">
              <div className="filter-group-modern">
                <label className="filter-label">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="M21 21l-4.35-4.35"></path>
                  </svg>
                  Search School
                </label>
                <div className="filter-input-wrapper">
                  <input
                    type="text"
                    name="search"
                    value={filters.search}
                    onChange={handleFilterChange}
                    placeholder="Search by name or address..."
                    className="filter-input-modern"
                  />
                </div>
              </div>
              <div className="filter-group-modern">
                <label className="filter-label">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                  City
                </label>
                <div className="filter-input-wrapper">
                  <select name="city" value={filters.city} onChange={handleFilterChange} className="filter-select-modern">
                    <option value="">All Cities</option>
                    {uniqueCities.map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="filter-group-modern">
                <label className="filter-label">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                  Board Type
                </label>
                <div className="filter-input-wrapper">
                  <select name="board_type" value={filters.board_type} onChange={handleFilterChange} className="filter-select-modern">
                    <option value="">All Boards</option>
                    {uniqueBoards.map(board => (
                      <option key={board} value={board}>{board}</option>
                    ))}
                    {!uniqueBoards.includes('Karnataka State') && (
                      <option value="Karnataka State">Karnataka State</option>
                    )}
                  </select>
                </div>
              </div>
            </div>
          </div>
          )}

          {error && (
            <div className="schools-error-message" role="alert">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <span>{error}</span>
              <button 
                onClick={fetchSchools} 
                className="retry-button"
                style={{
                  marginLeft: '12px',
                  padding: '6px 12px',
                  background: '#92400e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600'
                }}
              >
                Retry
              </button>
            </div>
          )}

          {loading ? (
            <div className="schools-loading">
              <div className="loading-spinner"></div>
              <p>Loading schools...</p>
            </div>
          ) : schools.length === 0 ? (
            <div className="schools-no-results">
              <div className="no-results-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="M21 21l-4.35-4.35"></path>
                </svg>
              </div>
              <h3>No Schools Found</h3>
              <p>Try adjusting your filters to find more schools.</p>
            </div>
          ) : (
            <div className="schools-grid-modern">
              {schools.map((school, index) => (
                <Link key={school.id} to={`/school/${school.id}`} className="school-card-modern" style={{ animationDelay: `${index * 0.05}s` }}>
                  <div className="school-card-image-wrapper">
                    <div className="school-image-container-modern">
                      {school.logo_url ? (
                        <img 
                          src={school.logo_url} 
                          alt={school.name} 
                          className="school-image-modern"
                          onError={(e) => {
                            e.target.src = getDefaultSchoolImage(school.board_type);
                          }}
                        />
                      ) : (
                        <img 
                          src={getDefaultSchoolImage(school.board_type)} 
                          alt={school.name} 
                          className="school-image-modern"
                        />
                      )}
                      <div className="school-image-overlay-modern"></div>
                      <div className="school-image-gradient"></div>
                    </div>
                    {school.logo_url && (
                      <div className="school-logo-badge-modern">
                        <img 
                          src={school.logo_url} 
                          alt={`${school.name} logo`}
                          onError={(e) => {
                            // Hide the logo badge if image fails to load
                            e.target.style.display = 'none';
                            e.target.parentElement.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    <div className="school-card-badge">
                      <span>{school.board_type}</span>
                    </div>
                  </div>
                  <div className="school-card-content-modern">
                    <h3 className="school-card-title">{school.name}</h3>
                    <div className="school-card-info">
                      <div className="school-info-item">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                          <circle cx="12" cy="10" r="3"></circle>
                        </svg>
                        <span>{school.city}</span>
                      </div>
                      {school.address && (
                        <div className="school-info-item">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                            <circle cx="12" cy="10" r="3"></circle>
                          </svg>
                          <span className="school-address-truncated">{school.address}</span>
                        </div>
                      )}
                    </div>
                    <div className="school-card-footer">
                      <span className="view-details">
                        View Details
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SchoolsDirectory;

